import type { ComponentProps } from 'react';

import {
	type FastshiftMigrationPlan,
	type FastshiftMigrationPlanPhase,
	type FastshiftMigrationPlanProduct,
	type FastshiftMigrationPlanStatus,
} from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { differenceInSeconds } from 'date-fns/differenceInSeconds';
import { isValid } from 'date-fns/isValid';
import { parseISO } from 'date-fns/parseISO';
import { type IntlShape, type MessageDescriptor } from 'react-intl';

import type Lozenge from '@atlaskit/lozenge';

import { getCurrentTime } from '@/common/utils/get-current-time';

import { messages } from './messages';

type LozengeAppearance = NonNullable<ComponentProps<typeof Lozenge>['appearance']>;

type MigrationPlanStatusConfig = { appearance: LozengeAppearance; message: MessageDescriptor };

const STATUS_CONFIG: Record<FastshiftMigrationPlanStatus, MigrationPlanStatusConfig> = {
	RUNNING: { appearance: 'inprogress', message: messages.statusRunning },
	COMPLETE: { appearance: 'success', message: messages.statusComplete },
	STOPPED: { appearance: 'default', message: messages.statusStopped },
	INCOMPLETE: { appearance: 'moved', message: messages.statusIncomplete },
	FAILED: { appearance: 'removed', message: messages.statusFailed },
	DRAFT: { appearance: 'default', message: messages.statusDraft },
	STARTING: { appearance: 'inprogress', message: messages.statusStarting },
	CHECK_STARTING: { appearance: 'inprogress', message: messages.statusCheckStarting },
	CHECK_RUNNING: { appearance: 'inprogress', message: messages.statusCheckRunning },
	CHECK_FAILED: { appearance: 'removed', message: messages.statusCheckFailed },
	CHECK_COMPLETED: { appearance: 'success', message: messages.statusCheckCompleted },
	CHECK_WARNING: { appearance: 'moved', message: messages.statusCheckWarning },
	ARCHIVED: { appearance: 'default', message: messages.statusArchived },
	UNARCHIVING: { appearance: 'default', message: messages.statusUnarchiving },
};

const UNKNOWN_STATUS_CONFIG: MigrationPlanStatusConfig = {
	appearance: 'default',
	message: messages.statusUnknown,
};

const STAGE_MESSAGES: Record<FastshiftMigrationPlanPhase, MessageDescriptor> = {
	PROD: messages.stageProd,
	TEST: messages.stageTest,
};

const PRODUCT_MESSAGES: Partial<Record<FastshiftMigrationPlanProduct, MessageDescriptor>> = {
	JIRA: messages.productJira,
	CONFLUENCE: messages.productConfluence,
};

export const getMigrationPlanStatusConfig = (status: FastshiftMigrationPlanStatus | null) =>
	(status && STATUS_CONFIG[status]) || UNKNOWN_STATUS_CONFIG;

export const getMigrationPlanStageMessage = (stage: FastshiftMigrationPlanPhase | null) =>
	(stage && STAGE_MESSAGES[stage]) || messages.stageUnknown;

export const getMigrationPlanProductMessage = (product: FastshiftMigrationPlanProduct | null) =>
	(product && PRODUCT_MESSAGES[product]) || messages.productUnknown;

export const getMigrationPlanElapsedSeconds = (
	{ status, started, updated }: Pick<FastshiftMigrationPlan, 'status' | 'started' | 'updated'>,
	now: number = getCurrentTime(),
): number | null => {
	if (status === 'DRAFT' || !started) {
		return null;
	}

	const start = parseISO(started);
	const end = status !== 'RUNNING' && updated ? parseISO(updated) : new Date(now);

	if (!isValid(start) || !isValid(end) || start > end) {
		return null;
	}

	return differenceInSeconds(end, start);
};

const ELAPSED_TIME_UNITS = [
	{ seconds: 86400, message: messages.elapsedTimeDays },
	{ seconds: 3600, message: messages.elapsedTimeHours },
	{ seconds: 60, message: messages.elapsedTimeMinutes },
	{ seconds: 1, message: messages.elapsedTimeSeconds },
];

export const formatMigrationPlanElapsedTime = (
	totalSeconds: number,
	formatMessage: IntlShape['formatMessage'],
): string => {
	let remainingSeconds = totalSeconds;
	let hasLargerUnit = false;

	return ELAPSED_TIME_UNITS.flatMap(({ seconds, message }) => {
		const value = Math.floor(remainingSeconds / seconds);
		remainingSeconds %= seconds;

		if (value > 0 || hasLargerUnit || seconds === 1) {
			hasLargerUnit = true;
			return formatMessage(message, { value });
		}
		return [];
	}).join(' ');
};
